import type { AuditEventQuery, ControlClient } from "../api/client.ts";
import type { AuditLogRow } from "../api/parse.ts";
import { localDayEndUtcIso, localDayStartUtcIso, sessionLocalDateKey } from "./sessionWhen.ts";

export type AuditFilters = {
  from: string;
  to: string;
};

export const EMPTY_AUDIT_FILTERS: AuditFilters = {
  from: "",
  to: "",
};

const FILTER_KEYS = ["from", "to"] as const;
const DAY = /^\d{4}-\d{2}-\d{2}$/;
const PAGE_CAP = 20;
export const AUDIT_PAGE_CAP = PAGE_CAP;
export const AUDIT_REFETCH_DEBOUNCE_MS = 400;

export type AuditCollection = {
  items: AuditLogRow[];
  truncated: boolean;
};

export function readAuditFilters(search: string): AuditFilters {
  const params = new URLSearchParams(search);
  return {
    from: params.get("from") ?? "",
    to: params.get("to") ?? "",
  };
}

export function auditSearch(current: string, filters: AuditFilters): string {
  const params = new URLSearchParams(current);
  for (const key of FILTER_KEYS) {
    const value = filters[key].trim();
    if (value.length === 0) params.delete(key);
    else params.set(key, value);
  }
  for (const legacy of ["who", "type", "device", "session", "detail"] as const) {
    params.delete(legacy);
  }
  const query = params.toString();
  return query.length === 0 ? "" : `?${query}`;
}

export function writeAuditFilters(filters: AuditFilters): void {
  const next = `${window.location.pathname}${auditSearch(window.location.search, filters)}`;
  window.history.replaceState(window.history.state, "", next);
}

export function auditRowVisible(row: { occurredAt: string }, filters: AuditFilters, timeZone?: string): boolean {
  const localDay = sessionLocalDateKey(row.occurredAt, timeZone);
  if (localDay.length === 0) return !(DAY.test(filters.from) || DAY.test(filters.to));
  if (DAY.test(filters.from) && localDay < filters.from) return false;
  if (DAY.test(filters.to) && localDay > filters.to) return false;
  return true;
}

export function auditServerQuery(filters: AuditFilters, timeZone?: string): AuditEventQuery | null {
  if (!DAY.test(filters.from) && !DAY.test(filters.to)) return null;
  const query: AuditEventQuery = { limit: 200 };
  if (DAY.test(filters.from)) {
    const from = localDayStartUtcIso(filters.from, timeZone);
    if (from.length > 0) query.from = from;
  }
  if (DAY.test(filters.to)) {
    const to = localDayEndUtcIso(filters.to, timeZone);
    if (to.length > 0) query.to = to;
  }
  return query.from || query.to ? query : null;
}

export function serverAuditKey(filters: AuditFilters, timeZone?: string): string {
  const query = auditServerQuery(filters, timeZone);
  if (!query) return "";
  return [query.from ?? "", query.to ?? "", timeZone ?? ""].join("\0");
}

function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) throw new DOMException("The operation was aborted.", "AbortError");
}

export async function collectAuditEvents(
  client: Pick<ControlClient, "listAuditEvents">,
  filters: AuditFilters,
  signal?: AbortSignal,
  timeZone?: string,
): Promise<AuditCollection> {
  throwIfAborted(signal);
  const query = auditServerQuery(filters, timeZone);
  if (!query) {
    const page = signal ? await client.listAuditEvents(undefined, signal) : await client.listAuditEvents();
    return { items: page.items, truncated: false };
  }
  const items: AuditLogRow[] = [];
  let cursor: string | undefined;
  for (let page = 0; page < PAGE_CAP; page += 1) {
    throwIfAborted(signal);
    const result = signal
      ? await client.listAuditEvents({ ...query, cursor }, signal)
      : await client.listAuditEvents({ ...query, cursor });
    items.push(...result.items);
    if (!result.nextCursor) return { items, truncated: false };
    cursor = result.nextCursor;
  }
  return { items, truncated: true };
}
